import {
  BetStatus,
  iframeService,
  applicationStore,
  notificationStore,
  DefaultQueryParams,
  parseQuery,
  GameModes,
  freeBetStore,
  UpdatedFreeBetDataItemKeys,
  userSettingsStore,
  loadingStore,
  popupStore,
  userInfoStore,
  roundInfoStore,
  gameConfigStore,
  betStore,
  gameResultStore,
  profileAvatarStore,
} from '@pascal/pascal-core';

import { worker } from '../index';
import { gameStore } from '../store/gameStore';
import { liveConnection } from '../core/liveConnection';
import { doBetStore } from '../store/doBetStore';
import { ANIMATION_DURATION } from '../core/constants/animationConfig';
import { gameLiveConnection } from './gameLiveConnection';
import { soundService, soundTypes } from './soundService';
import { TYPE_SHARK } from '../enums/uiConfig';
import { gameMainDrawer } from '../Game/GameDrawer/gameMainDrawer';
import { brandedGameNameDependOnIds } from '../enums/brandedPartners';

let timeOutId;
let timeId = null;
let timeOutIdResultReport = null;

class GameService {
  onGetInitialStateResult({
    rate,
    limits,
    results,
    balance,
    precision,
    roundInfo,
    avatarId,
    currencyCode,
    currentBets,
    underMaintenance,
    partnerWageringOdd,
  }) {
    const { [DefaultQueryParams.Mode]: mode } = parseQuery(window.location.search);
    this.sendWorker(null);
    gameStore.resetGameData();
    gameMainDrawer.resetAllAnimations();

    if (underMaintenance) {
      const { startDate, notifySeconds } = underMaintenance;
      popupStore.startUnderMaintenanceFunctional({
        startDate,
        notifySeconds,
      });
    }

    if (mode === GameModes.Fun) {
      applicationStore.setIsDemo(true);
    }
    gameStore.setWageringOdd(partnerWageringOdd);
    const currentRound = results.find((item) => item.roundId === roundInfo.roundId);
    const roundData = currentRound?.roundData?.reverse() || [];
    profileAvatarStore.updateAvatarId(avatarId);
    profileAvatarStore.updateInitialAvatarId(avatarId);
    gameStore.setReportResult(roundData);
    roundInfoStore.setRoundInfo(roundInfo);
    gameConfigStore.setLimits(limits);
    const isCurrentRoundFinished = roundData.every((item) => item.odd);
    let time;
    clearTimeout(timeId);
    clearTimeout(timeOutIdResultReport);

    if (roundInfo.second < 0) {
      gameStore.setTimerValue(roundInfo.duration);
      time = Math.abs(roundInfo.second % roundInfo.duration);
      clearTimeout(timeId);
      if (time > roundInfo.duration / 2) {
        if (isCurrentRoundFinished) {
          doBetStore.setDisabledCashOutButton({ 0: false, 1: false });
          doBetStore.setDisabledBetButton({ 0: false, 1: false });
          this.sendWorker(roundInfo.duration / 2 + ANIMATION_DURATION - Math.round(time));
        }
      } else {
        if (isCurrentRoundFinished) {
          timeId = setTimeout(() => {
            if (isCurrentRoundFinished) {
              doBetStore.setDisabledBetButton({ 0: false, 1: false });
              doBetStore.setDisabledCashOutButton({ 0: false, 1: false });
              this.sendWorker(roundInfo.duration / 2);
            }
            clearTimeout(timeId);
          }, ANIMATION_DURATION * 1000);
        }
      }
    } else {
      this.sendWorker(Math.floor(roundInfo.second));
      gameStore.setTimerValue(roundInfo.second);
    }
    if (currentBets?.length) {
      const filteredCurrentBets = currentBets.filter((bet) => bet.status === BetStatus.Pending);
      filteredCurrentBets.reverse();
      betStore.setLiveBets(filteredCurrentBets);

      const userCurrentBets = currentBets.filter(
        (bet) => +bet.userId === +userInfoStore.userId && bet.status === BetStatus.Pending
      );

      if (userCurrentBets && userCurrentBets.length) {
        const userBets = doBetStore.userBets;
        userCurrentBets.forEach((bet) => {
          const currentFreeBet = bet?.freeBetId
            ? freeBets?.find((item) => Number(item?.id) === Number(bet?.freeBetId))
            : null;
          userBets[bet.betData.index] = bet;
          doBetStore.updateMaxPayoutBetData({
            sectionId: bet?.betData?.index,
            updatedDataByKeys: {
              [UpdatedFreeBetDataItemKeys.IS_BET_ACCEPTED]: true,
              [UpdatedFreeBetDataItemKeys.FREE_BET_ID]: bet?.freeBetId,
              [UpdatedFreeBetDataItemKeys.BET_AMOUNT]: Number(bet?.amount),
              [UpdatedFreeBetDataItemKeys.CURRENT_INDEX]: bet?.betData?.index,
              [UpdatedFreeBetDataItemKeys.FREE_BET_TYPE]: bet?.freeBetInfo
                ? bet?.freeBetInfo?.winType
                : currentFreeBet?.winType,
              [UpdatedFreeBetDataItemKeys.FREE_BET_MIN_ODD]: bet?.freeBetInfo?.minOdd,
              [UpdatedFreeBetDataItemKeys.MAX_PAYOUT]: bet?.freeBetId
                ? Number(bet?.freeBetInfo?.maxPayout) || Number(currentFreeBet?.maxPayout)
                : Number(limits.maxWin),
            },
          });
        });
        doBetStore.setUserBets(userBets);
      }
    } else {
      iframeService.sendStakeUpdate(0);
    }
    gameStore.setCurrentRound(roundInfo);
    gameStore.setCurrentRoundId(roundInfo.roundId);
    loadingStore.updateLoading({ isGameInitialLoaded: true });
    popupStore.close();
    popupStore.toggleReconnect(false);
    roundInfoStore.setRoundInfo(roundInfo);
    gameConfigStore.setLimits(limits);
    gameResultStore.setGameResults(results);
    userInfoStore.setUserInfo({
      rate,
      balance,
      precision,
      currencyCode,
    });
    const { gameId } = gameConfigStore;

    if (brandedGameNameDependOnIds[gameId]) {
      gameMainDrawer.playMarlinAnimation();
    }
  }

  sendBet(data) {
    doBetStore.setDisabledBetButton({
      ...doBetStore.disabledBetButton,
      [data.betData.index]: true,
    });
    doBetStore.setDisabledCashOutButton({
      ...doBetStore.disabledCashOutButton,
      [data.betData.index]: true,
    });
    gameLiveConnection.doBet(data);
    // }
  }

  handleDoBetResult(data) {
    const { userId } = userInfoStore;
    const {
      responseCode,
      freeBetId,
      freeBetInfo,
      amount,
      betData: { index },
    } = data;
    const { isSoundOn } = userSettingsStore;
    const { repliedBetData } = betStore;
    const { freeBets } = freeBetStore;
    if (Number(userId) === Number(data.userId) || repliedBetData) {
      // iframeService.sendStakeUpdate(doBetStore?.totalBetAmount);
      doBetStore.setDisabledBetButton({
        ...doBetStore.disabledBetButton,
        [data.betData.index]: false,
      });
      const userBets = doBetStore.userBets;
      if (repliedBetData) {
        userBets.amount = repliedBetData?.amount;
      }
      userBets[data.betData.index] = data;
      doBetStore.setUserBets(userBets);

      notificationStore.setNotificationType({ responseCode });
      doBetStore.setDisabledCashOutButton({
        ...doBetStore.disabledCashOutButton,
        [data.betData.index]: true,
      });

      if (isSoundOn) {
        soundService.playBetAccepted();
      }
      const currentFreeBet = freeBetId
        ? freeBets?.find((item) => Number(item?.id) === Number(freeBetId))
        : null;

      if (freeBetId && (freeBetInfo?.maxPayout || currentFreeBet?.maxPayout)) {
        doBetStore.updateMaxPayoutBetData({
          sectionId: index,
          updatedDataByKeys: {
            [UpdatedFreeBetDataItemKeys.IS_BET_ACCEPTED]: true,
            [UpdatedFreeBetDataItemKeys.FREE_BET_ID]: freeBetId,
            [UpdatedFreeBetDataItemKeys.BET_AMOUNT]: Number(amount),
            [UpdatedFreeBetDataItemKeys.CURRENT_INDEX]: index,
            [UpdatedFreeBetDataItemKeys.FREE_BET_TYPE]: freeBetInfo
              ? freeBetInfo?.winType
              : currentFreeBet?.winType,
            [UpdatedFreeBetDataItemKeys.FREE_BET_MIN_ODD]: currentFreeBet?.minOdd,
            [UpdatedFreeBetDataItemKeys.MAX_PAYOUT]: freeBetInfo
              ? Number(freeBetInfo?.maxPayout)
              : Number(currentFreeBet?.maxPayout),
            // [UpdatedFreeBetDataItemKeys.IS_AUTO_CASH_OUT]: !!betData?.autoCashOutOdd,
          },
        });
      } else {
        doBetStore.updateMaxPayoutBetData({
          sectionId: index,
          updatedDataByKeys: {
            [UpdatedFreeBetDataItemKeys.IS_BET_ACCEPTED]: true,
            [UpdatedFreeBetDataItemKeys.BET_AMOUNT]: Number(amount),
            [UpdatedFreeBetDataItemKeys.CURRENT_INDEX]: index,
            [UpdatedFreeBetDataItemKeys.MAX_PAYOUT]: +gameConfigStore?.limits.maxWin,
          },
        });
      }
    }
    betStore.updateLiveBets(data);
  }

  handleDoBetResultValidation(data) {
    const { responseCode, betData } = data;

    notificationStore.setNotificationType({ responseCode });

    if (betData) {
      doBetStore.setDisabledBetButton({
        ...doBetStore.disabledBetButton,
        [betData.index]: false,
      });
    }
  }

  handleRoundStart({ data: { startedRound, results } }) {
    doBetStore.resetMaxPayoutData();
    gameStore.setRoundStarted(true);
    doBetStore.setDisabledBetButton({ 0: false, 1: false });
    this.sendWorker(startedRound.duration);
    gameStore.setReportResult([]);
    gameStore.setCurrentRound(startedRound);
    gameStore.setCurrentRoundId(startedRound.roundId);
    gameResultStore.setGameResults(results);

    if (doBetStore.userBets !== null) {
      doBetStore.setUserBets({ 0: null, 1: null });
    }

    if (betStore.liveBets?.length) {
      betStore.setLiveBets([]);
    }
  }

  handleResultReport({ data }) {
    clearTimeout(timeId);
    clearTimeout(timeOutIdResultReport);
    this.sendWorker(null);
    const { resultReportData } = data;
    const { last, prevReports } = gameStore._getPrevReportResultAndLastElement(
      resultReportData.reverse()
    );
    const { gameId } = gameConfigStore;
    gameStore.setReportResult(prevReports);
    if (last) {
      gameStore.setReportResultWithAnimation(last);
      const updateReportData = [gameStore?.currentReportResultData, ...prevReports];

      // gameStore.setFishState(last.resultType, 0, last.odd);
      if (last.resultType === TYPE_SHARK) {
        gameMainDrawer.playSharkAnimation();
        gameMainDrawer.playFishAnimation(last.resultType);
        setTimeout(() => {
          gameMainDrawer.playBackgroundShakeAnimation();
        }, 200);
        soundService?.play(soundTypes.sharkComing);
        setTimeout(() => {
          gameMainDrawer.updateOddText(last.odd);
        }, 500);
        if (brandedGameNameDependOnIds[gameId]) {
          setTimeout(() => {
            gameMainDrawer.playTransitionAnimation();
          }, 1000);
        }

        setTimeout(() => {
          gameMainDrawer.resetSkeletonFromFishSpine();
          gameMainDrawer.stopFishAnimation();
          gameMainDrawer.playCharacterStaticAnimation();
          gameMainDrawer.playBackgroundStaticAnimation();
          // fishPopupDrawer.playFishStaticAnimation();
          // this.updateGameStoreAfterResultReport(lastFish, resultReportData);
        }, 3000);
      } else {
        gameMainDrawer.playCharacterFishingAnimation();
        gameMainDrawer.playFishAnimation(last.resultType);
        soundService?.play(soundTypes.fishComing);
        setTimeout(() => {
          gameMainDrawer.updateOddText(last.odd);
        }, 500);
        setTimeout(() => {
          gameMainDrawer.resetSkeletonFromFishSpine();
          // gameMainDrawer.resetOddText();
          gameMainDrawer.stopFishAnimation();
          gameMainDrawer.playCharacterStaticAnimation();
          // this.updateGameStoreAfterResultReport(lastFish, resultReportData);
        }, 3000);
      }

      setTimeout(() => {
        gameStore.setReportResult(updateReportData);
      }, 2000);
      //
    }

    doBetStore.setDisabledCashOutButton({ 0: true, 1: true });
    doBetStore.setDisabledBetButton({ 0: true, 1: true });
    const { duration } = gameStore?.currentRound;
    const fishType = resultReportData[0].resultType;
    timeOutIdResultReport = setTimeout(() => {
      if (fishType !== TYPE_SHARK) {
        this.sendWorker(duration / 2);
        doBetStore.setDisabledCashOutButton({ 0: false, 1: false });
        doBetStore.setDisabledBetButton({ 0: false, 1: false });
      } else {
        doBetStore.setDisabledCashOutButton({ 0: true, 1: true });
        doBetStore.setDisabledBetButton({ 0: true, 1: true });
      }
    }, ANIMATION_DURATION * 1000);
    gameStore.setShowResultAnimation(true);
  }

  handleRoundFin({ data }) {
    this.sendWorker(null);
    doBetStore.setDisabledBetButton({ 0: true, 1: true });
    doBetStore.setDisabledCashOutButton({ 0: true, 1: true });
    const allBets = betStore.liveBets.map((bet) => {
      const finishedBet = data?.bets?.find((item) => item.id === bet.id);
      return finishedBet
        ? { ...finishedBet, isRoundFinish: true }
        : { ...bet, isRoundFinish: true };
    });

    gameStore.setRoundStarted(false);
    // gameStore.resetFishState();
    betStore.setLiveBets(allBets);
    const finishedResultsHistory = data.finishedRound;

    const updatedResultHistoryData = gameResultStore?.gameResults?.map((item) => {
      if (item.roundId === finishedResultsHistory.id) {
        const { id, ...rest } = finishedResultsHistory;
        return { ...item, ...rest };
      }
      return item;
    });

    setTimeout(() => {
      doBetStore.setUserBets({ 0: null, 1: null });
      betStore.setLiveBets([]);
    }, 3000);
    gameResultStore?.setGameResults([...updatedResultHistoryData]);
  }

  handleBetsClosed() {
    doBetStore.setDisabledBetButton({ 0: true, 1: true });
  }

  handleCashOutResult(cashOutData) {
    const { userId } = userInfoStore;
    const { repliedBetData } = betStore;
    const { isSoundOn } = userSettingsStore;

    if (cashOutData?.userId === userId || repliedBetData) {
      doBetStore.setWin(null);
      const userBets = doBetStore.userBets;
      doBetStore.setDisabledCashOutButton({
        ...doBetStore.disabledCashOutButton,
        [cashOutData.betData?.index]: false,
      });
      if (isSoundOn) {
        soundService.play(soundTypes.winBeep);
        soundService.playWin();
      }
      userBets[cashOutData.additionalParams.index] = null;
      doBetStore.setWin(cashOutData);
      doBetStore.setUserBets(userBets);
      doBetStore.updateMaxPayoutBetData({
        sectionId: Number(cashOutData?.additionalParams?.index),
        updatedDataByKeys: {
          [UpdatedFreeBetDataItemKeys.IS_SHOW_POP_UP]: false,
          [UpdatedFreeBetDataItemKeys.IS_ALREADY_SEEN_POP_UP]: true,
          [UpdatedFreeBetDataItemKeys.MAX_PAYOUT]: null,
          [UpdatedFreeBetDataItemKeys.FREE_BET_ID]: null,
          [UpdatedFreeBetDataItemKeys.FREE_BET_TYPE]: null,
          [UpdatedFreeBetDataItemKeys.CURRENT_INDEX]: null,
          [UpdatedFreeBetDataItemKeys.FREE_BET_MIN_ODD]: null,
          [UpdatedFreeBetDataItemKeys.IS_BET_ACCEPTED]: false,
          [UpdatedFreeBetDataItemKeys.BET_AMOUNT]: null,
          [UpdatedFreeBetDataItemKeys.CASH_OUT_AMOUNT]: null,
          [UpdatedFreeBetDataItemKeys.IS_AUTO_CASH_OUT]: false,
          [UpdatedFreeBetDataItemKeys.IS_HALF_CASH_OUT]: false,
        },
      });
      setTimeout(() => {
        doBetStore.setWin(null);
      }, 2000);
      setTimeout(() => {
        gameLiveConnection.getBalance();
      }, 1000);
    }
    const bets = betStore.liveBets;
    const index = bets.findIndex((bet) => bet.id === cashOutData.id);
    const finishedAnimationData = { ...cashOutData, isRoundFinish: true };
    bets.splice(index, 1, finishedAnimationData);
    betStore.setLiveBets([...bets]);
  }

  countDown({ data: time }) {
    gameStore.setTime(time);
  }

  handleReconnecting(error) {
    if (error && error.message) {
      popupStore.toggleReconnect();
    } else {
      GameService.ReconnectCount += 1;
      popupStore.toggleReconnect();
    }
  }

  handleReconnected() {
    GameService.ReconnectCount = 0;
    gameLiveConnection.sendGameGetInitialState(false);
  }

  handleConnectionError(e) {
    console.error(e, 'error onClose');
    if (GameService.ReconnectCount) {
      popupStore.onConnectionError(true);
    }
  }

  socketRecursiveReconnecting() {
    try {
      liveConnection
        .restartGameConnection()
        .then((connectionData) => {
          popupStore.connectionError && popupStore.onConnectionError();
          liveConnection.connectionInitCallback(connectionData);
          clearTimeout(timeOutId);
        })
        .catch(() => {
          timeOutId = setTimeout(() => {
            this.socketRecursiveReconnecting();
          }, 4000);
        });
    } catch (err) {
      popupStore.toggleReconnect(true);
    }
  }

  sendWorker(timerValue) {
    if (timerValue >= 0) {
      worker.addEventListener('message', this.countDown);
      worker.postMessage(timerValue);
    } else {
      this.countDown({ data: null });
      worker.postMessage(null);
      worker.removeEventListener('message', this.countDown);
    }
  }
}

export const gameService = new GameService();
